// src/pages/ActivityLogPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useOrgId } from '../utils/tenant';

function fmtTs(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : null);
    if (!d) return '';
    return d.toLocaleString();
  } catch {
    return '';
  }
}

export default function ActivityLogPage({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();
  const [rows, setRows] = useState([]);
  const [qText, setQText] = useState('');
  const siteScopeKey = `${siteScope.allSites ? 'ALL' : 'SCOPED'}:${(siteScope.siteIds || []).slice().sort().join('|')}`;

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;

    (async () => {
      const ref = collection(db, 'orgs', orgId, 'auditLogs');
      let qq;
      if (siteScope.allSites) {
        qq = query(ref, orderBy('ts', 'desc'), limit(200));
      } else {
        const allowed = Array.from(new Set(siteScope.siteIds || [])).filter(Boolean);
        if (!allowed.length) {
          setRows([]);
          return;
        }
        // Firestore `in` supports a bounded list. Management site assignments are
        // intentionally narrow; querying by storeId also prevents organisation-wide
        // / unscoped audit events from being exposed to delegated managers.
        qq = query(ref, where('storeId', 'in', allowed.slice(0, 30)), orderBy('ts', 'desc'), limit(200));
      }
      const snap = await getDocs(qq);
      if (cancelled) return;
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })().catch((error) => {
      console.error('[Activity Log] Failed to load scoped activity', error);
      if (!cancelled) setRows([]);
    });

    return () => { cancelled = true; };
  }, [orgId, siteScopeKey]);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => {
      const hay = [
        r.action, r.page, r.entityType, r.entityName, r.entityId,
        r.performedByName, r.performedByEmail, r.storeId
      ].map((x) => String(x || '').toLowerCase()).join(' | ');
      return hay.includes(t);
    });
  }, [rows, qText]);

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white">Activity Log</h1>
          <div className="text-sm text-white/60">
            Track who changed what and when (latest 200 events).
          </div>
        </div>

        <div className="w-full max-w-sm">
          <input
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Search… (name, action, page, store)"
            className="w-full px-3 py-2 rounded-lg border border-white/10 bg-transparent text-white placeholder:text-white/40"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="grid grid-cols-12 gap-0 text-xs text-white/60 bg-white/5 px-4 py-2">
          <div className="col-span-3">When</div>
          <div className="col-span-2">By</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-2">Page</div>
          <div className="col-span-3">Target</div>
        </div>

        <div className="divide-y divide-white/10">
          {filtered.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-0 px-4 py-3 text-sm">
              <div className="col-span-3 text-white/70">{fmtTs(r.ts)}</div>
              <div className="col-span-2 text-white">
                <div className="font-semibold">{r.performedByName || 'Unknown'}</div>
                <div className="text-xs text-white/50">{r.performedByEmail || ''}</div>
              </div>
              <div className="col-span-2 text-white/90">{r.action || ''}</div>
              <div className="col-span-2 text-white/70">{r.page || ''}</div>
              <div className="col-span-3 text-white/80">
                <div className="font-semibold">{r.entityName || r.entityId || ''}</div>
                <div className="text-xs text-white/50">
                  {(r.entityType ? `${r.entityType}` : '')}{r.storeId ? ` • ${r.storeId}` : ''}
                </div>
              </div>
            </div>
          ))}

          {!filtered.length && (
            <div className="px-4 py-6 text-sm text-white/60">
              No activity yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
